import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "264812955979";

const initialForm = {
  name: "",
  contact: "",
  room: "Standard Room",
  check_in: "",
  check_out: "",
  guests: "2",
  notes: "",
};

const inputCls =
  "w-full px-4 py-3 bg-[#F7F6F2] border border-[#D5D0C2] text-sm text-[#1A1A18] rounded-lg focus:outline-none focus:border-[#9E7D4E] transition-colors";
const labelCls =
  "block font-mono-tag text-[10px] uppercase tracking-wider text-[#55524A] mb-1";

export const Booking = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const whatsappHref = () => {
    const lines = [
      `Hello Tse Yaa Kuku, I would like to enquire about a booking.`,
      `Name: ${form.name || "-"}`,
      `Room: ${form.room}`,
      form.check_in && `Check-in: ${form.check_in}`,
      form.check_out && `Check-out: ${form.check_out}`,
      `Guests: ${form.guests}`,
      form.notes && `Notes: ${form.notes}`,
    ].filter(Boolean);
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSubmitted(true);
      toast.success("Enquiry sent — we will confirm your dates shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Could not send your enquiry. Please try WhatsApp instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="py-24 bg-[#EBE7DD] border-t border-[#DCD6C8] px-8"
    >
      <div className="max-w-5xl mx-auto bg-white p-8 md:p-14 rounded-3xl border border-[#D5D0C2] shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <span className="font-mono-tag text-xs tracking-widest text-[#9E7D4E] uppercase">
              Direct Inquiries
            </span>
            <h2 className="font-editorial text-3xl sm:text-4xl text-[#1A1A18] mt-2 mb-4">
              Reach Tse Yaa Kuku
            </h2>
            <p className="font-grotesk text-sm text-[#55524A] mb-6">
              We confirm dates quickly via WhatsApp or direct email enquiry.
            </p>

            <div className="space-y-4 font-grotesk text-sm text-[#1A1A18] mb-8">
              <div className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-[#9E7D4E]" />
                <span data-testid="booking-address">Hindenburg Street, Otjiwarongo, Namibia</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#9E7D4E]" />
                <a href="tel:+264812955979" data-testid="booking-phone" className="hover:underline">
                  +264 81 295 5979
                </a>
              </div>
            </div>

            <a
              href={whatsappHref()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-whatsapp-button"
              className="inline-flex items-center justify-center gap-2 w-full py-4 bg-[#25D366] text-white font-grotesk text-xs uppercase tracking-widest hover:bg-[#1EBE5D] transition-colors rounded-xl font-medium"
            >
              Connect on WhatsApp (+264 81 295 5979)
            </a>
          </div>

          <form data-testid="booking-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="enquiry-name" className={labelCls}>Your Name</label>
              <input
                id="enquiry-name"
                data-testid="booking-name-input"
                type="text"
                required
                value={form.name}
                onChange={set("name")}
                className={inputCls}
                placeholder="Guest name"
              />
            </div>
            <div>
              <label htmlFor="enquiry-contact" className={labelCls}>Contact Email / Phone</label>
              <input
                id="enquiry-contact"
                data-testid="booking-contact-input"
                type="text"
                required
                value={form.contact}
                onChange={set("contact")}
                className={inputCls}
                placeholder="+264 ..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="enquiry-checkin" className={labelCls}>Check in</label>
                <input
                  id="enquiry-checkin"
                  data-testid="booking-checkin-input"
                  type="date"
                  value={form.check_in}
                  onChange={set("check_in")}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="enquiry-checkout" className={labelCls}>Check out</label>
                <input
                  id="enquiry-checkout"
                  data-testid="booking-checkout-input"
                  type="date"
                  value={form.check_out}
                  onChange={set("check_out")}
                  className={inputCls}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="enquiry-room" className={labelCls}>Selected Room</label>
                <select
                  id="enquiry-room"
                  data-testid="booking-room-select"
                  value={form.room}
                  onChange={set("room")}
                  className={inputCls}
                >
                  <option>Standard Room</option>
                  <option>Comfort Room</option>
                  <option>Family Room</option>
                </select>
              </div>
              <div>
                <label htmlFor="enquiry-guests" className={labelCls}>Guests</label>
                <select
                  id="enquiry-guests"
                  data-testid="booking-guests-select"
                  value={form.guests}
                  onChange={set("guests")}
                  className={inputCls}
                >
                  <option>1</option>
                  <option>2</option>
                  <option>3</option>
                  <option>4+</option>
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="enquiry-notes" className={labelCls}>Notes</label>
              <textarea
                id="enquiry-notes"
                data-testid="booking-notes-input"
                rows={2}
                value={form.notes}
                onChange={set("notes")}
                className={`${inputCls} resize-none`}
                placeholder="Anything we should know?"
              />
            </div>
            <button
              type="submit"
              data-testid="booking-submit-button"
              disabled={submitting}
              className="w-full py-4 bg-[#0A192F] text-white font-grotesk text-xs uppercase tracking-widest hover:bg-[#9E7D4E] transition-colors rounded-lg disabled:opacity-60"
            >
              {submitting ? "Sending..." : "Submit Booking Enquiry"}
            </button>
            {submitted && (
              <p data-testid="booking-success-message" className="text-xs text-[#9E7D4E] font-grotesk">
                Thank you — your enquiry has been received.
              </p>
            )}
          </form>
        </div>
      </div>

      <div className="max-w-5xl mx-auto mt-10 rounded-2xl overflow-hidden border border-[#D5D0C2] shadow-sm">
        <iframe
          title="Tse Yaa Kuku Guesthouse on Google Maps — Hindenburg Street, Otjiwarongo"
          data-testid="booking-map"
          src="https://maps.google.com/maps?q=Hindenburg%20Street%2C%20Otjiwarongo%2C%20Namibia&t=&z=14&ie=UTF8&iwloc=&output=embed"
          className="w-full h-80 border-0"
          loading="lazy"
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </section>
  );
};
